import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Google as GoogleIcon,
  TableChart as SheetsIcon,
  Check as CheckIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { googleOAuthService } from '../services/googleOAuthService';
import { googleSheetsService } from '../services/googleSheetsService';
import { otdrService } from '../services/otdrService';

const GoogleSheetsOAuthDialog = ({ open, onClose, projects, onSyncComplete }) => {
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  // OAuth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Sheets state
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetTabs, setSheetTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState('');
  const [headers, setHeaders] = useState([]);

  // Configuration state
  const [selectedProject, setSelectedProject] = useState('');
  const [configName, setConfigName] = useState('');
  const [columnMappings, setColumnMappings] = useState({});

  // Field mapping options for OTDR
  const availableFields = [
    { key: 'order_id', label: 'ORDER ID', required: true, description: 'Column containing order IDs' },
    { key: 'shipment_id', label: 'SHIPMENT ID', required: true, description: 'Column containing shipment/tracking IDs' },
    { key: 'latest_delivery_date', label: 'LATEST DELIVERY DATE', required: true, description: 'Column containing expected/latest delivery dates' },
    { key: 'actual_delivered_date', label: 'ACTUAL DELIVERED DATE', required: true, description: 'Column containing actual delivery dates' }
  ];

  const steps = [
    'Google Authentication',
    'Select Sheet',
    'Configure Mapping',
    'Save Configuration'
  ];

  useEffect(() => {
    if (open) {
      checkAuthStatus();
    }
  }, [open]);

  // Add a state to trigger sheet loading
  const [shouldLoadSheets, setShouldLoadSheets] = useState(false);

  useEffect(() => {
    // Listen for OAuth popup messages
    const handleMessage = (event) => {
      console.log('Received message:', event.origin, event.data);
      
      // Allow messages from localhost (development) and production domain
      const allowedOrigins = ['localhost', 'tracking.trishoolinhouse.com'];
      const isAllowedOrigin = allowedOrigins.some(allowed => event.origin.includes(allowed));
      
      if (!isAllowedOrigin) {
        console.log('Origin not allowed:', event.origin);
        return;
      }
      
      if (event.data && event.data.type === 'oauth_success') {
        console.log('OAuth success detected, updating state...');
        setIsAuthenticated(true);
        setActiveStep(1);
        setAlert({ type: 'success', message: 'Successfully authenticated with Google!' });
        setAuthLoading(false);
        // Trigger sheet loading
        setShouldLoadSheets(true);
      } else if (event.data && event.data.type === 'oauth_error') {
        console.log('OAuth error detected:', event.data.message);
        setAlert({ type: 'error', message: event.data.message });
        setAuthLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Effect to load sheets when authentication is successful
  useEffect(() => {
    const loadSheetsAfterAuth = async () => {
      try {
        setLoading(true);
        const sheetsData = await googleOAuthService.getSheets();
        setSheets(sheetsData);
        setShouldLoadSheets(false); // Reset trigger
      } catch (error) {
        setAlert({ type: 'error', message: 'Failed to load Google Sheets' });
        setShouldLoadSheets(false); // Reset trigger
      } finally {
        setLoading(false);
      }
    };

    if (shouldLoadSheets && isAuthenticated) {
      loadSheetsAfterAuth();
    }
  }, [shouldLoadSheets, isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      const status = await googleOAuthService.getAuthStatus();
      setIsAuthenticated(status.authenticated);
      if (status.authenticated) {
        setActiveStep(1);
        // Trigger sheet loading
        setShouldLoadSheets(true);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setAuthLoading(true);
      const authData = await googleOAuthService.startOAuth();
      
      // Open OAuth URL in a new window
      window.open(authData.authorization_url, 'google-oauth', 'width=500,height=600');
      
      setAlert({ type: 'info', message: 'Please complete authentication in the popup window...' });
      // Don't set authLoading to false here - let the message handler do it
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to start Google authentication' });
      setAuthLoading(false);
    }
  };

  const loadSheets = async () => {
    try {
      setLoading(true);
      const sheetsData = await googleOAuthService.getSheets();
      setSheets(sheetsData);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to load Google Sheets' });
    } finally {
      setLoading(false);
    }
  };

  const selectSheet = async (sheet) => {
    try {
      setLoading(true);
      setSelectedSheet(sheet);
      
      // For OTDR, get headers from all tabs to enable proper mapping
      const allTabsData = await googleOAuthService.getAllTabsHeaders(sheet.id);
      
      // Use the first tab's headers as default for mapping setup
      if (allTabsData.tabs && allTabsData.tabs.length > 0) {
        const firstTab = allTabsData.tabs[0];
        setHeaders(firstTab.headers);
        setSelectedTab(firstTab.tab_name);
        
        // Store all tabs info for later processing
        const tabNames = allTabsData.tabs.map(tab => tab.tab_name);
        setSheetTabs(tabNames);
        
        // Skip to mapping step (step 2 -> step 3)
        setActiveStep(2);
        
        setAlert({ 
          type: 'success', 
          message: `Loaded headers from ${allTabsData.tabs.length} tabs. Showing "${firstTab.tab_name}" for mapping setup.` 
        });
      } else {
        throw new Error('No tabs found in the sheet');
      }
    } catch (error) {
      setAlert({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to load sheet headers' 
      });
    } finally {
      setLoading(false);
    }
  };

  const selectTab = async (tabName) => {
    try {
      setLoading(true);
      setSelectedTab(tabName);
      const headersData = await googleOAuthService.getSheetHeaders(selectedSheet.id, tabName);
      setHeaders(headersData);
      
      // Auto-generate config name
      if (!configName) {
        setConfigName(`${selectedSheet.name} - ${tabName}`);
      }
      
      setActiveStep(3);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to load sheet headers' });
    } finally {
      setLoading(false);
    }
  };

  const handleColumnMapping = (field, columnName) => {
    setColumnMappings(prev => ({
      ...prev,
      [field]: columnName
    }));
  };

  const saveConfiguration = async () => {
    try {
      // Validate required fields
      const requiredFields = availableFields.filter(field => field.required);
      const missingFields = requiredFields.filter(field => !columnMappings[field.key]);
      
      if (missingFields.length > 0) {
        setAlert({ 
          type: 'error', 
          message: `Please map required fields: ${missingFields.map(f => f.label).join(', ')}` 
        });
        return;
      }

      if (!selectedProject) {
        setAlert({ type: 'error', message: 'Please select a project' });
        return;
      }

      setLoading(true);

      // Create configuration data for OTDR extraction
      const configData = {
        project_id: parseInt(selectedProject),
        sheet_name: configName,
        sheet_id: selectedSheet.id,
        column_mappings: columnMappings
      };

      // Save OTDR configuration
      const otdrConfig = await otdrService.createConfig(configData);
      
      // Automatically extract and import OTDR data from all tabs using OAuth
      try {
        const extractionResult = await googleOAuthService.extractOtdrDataAllTabs(selectedSheet.id, configData);
        setAlert({ 
          type: 'success', 
          message: `OTDR configuration saved and data imported successfully from ${extractionResult.tabs_processed}/${extractionResult.total_tabs} tabs! Created ${extractionResult.shipments_created || 0} shipments, updated ${extractionResult.shipments_updated || 0} shipments.` 
        });
      } catch (extractError) {
        console.error('Extract error:', extractError);
        setAlert({ 
          type: 'warning', 
          message: `OTDR configuration saved but data import failed: ${extractError.message}. You can try manual sync later.` 
        });
      }
      
      // Call sync complete callback if provided
      if (onSyncComplete) {
        onSyncComplete();
      }
      
      // Reset form and close after delay
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      console.error('Save configuration error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save configuration';
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setActiveStep(0);
    setIsAuthenticated(false);
    setSheets([]);
    setSelectedSheet(null);
    setSheetTabs([]);
    setSelectedTab('');
    setHeaders([]);
    setSelectedProject('');
    setConfigName('');
    setColumnMappings({});
    setAlert(null);
    onClose();
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (activeStep === 1 && !isAuthenticated) {
      checkAuthStatus();
    } else if (activeStep === 1 && sheets.length === 0) {
      // Trigger sheet loading if not already loaded
      setShouldLoadSheets(true);
    } else {
      // Progress to next step
      setActiveStep(prev => prev + 1);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <GoogleIcon color="primary" />
          Google Sheets Integration Setup
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {alert && (
          <Alert 
            severity={alert.type} 
            onClose={() => setAlert(null)}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Google Authentication */}
          <Step>
            <StepLabel>Google Authentication</StepLabel>
            <StepContent>
              <Box py={2}>
                {!isAuthenticated ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Connect to Google Account
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Authenticate with Google to access your spreadsheets
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="contained"
                        startIcon={authLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
                        onClick={handleGoogleLogin}
                        disabled={authLoading}
                        color="primary"
                      >
                        {authLoading ? 'Connecting...' : 'Connect with Google'}
                      </Button>
                    </CardActions>
                  </Card>
                ) : (
                  <Alert severity="success" icon={<CheckIcon />}>
                    Successfully authenticated with Google! Click Next to proceed.
                  </Alert>
                )}
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Select Sheet */}
          <Step>
            <StepLabel>Select Sheet</StepLabel>
            <StepContent>
              <Box py={2}>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : sheets.length > 0 ? (
                  <Grid container spacing={2}>
                    {sheets.map((sheet) => (
                      <Grid item xs={12} sm={6} key={sheet.id}>
                        <Card 
                          variant="outlined"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            ...(selectedSheet?.id === sheet.id && { 
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText'
                            })
                          }}
                          onClick={() => selectSheet(sheet)}
                        >
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <SheetsIcon color="success" />
                              <Typography variant="h6" noWrap>
                                {sheet.name}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              ID: {sheet.id}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography color="text.secondary">
                      No sheets found. Click Next to load your Google Sheets.
                    </Typography>
                  </Box>
                )}

                {selectedSheet && (
                  <Box mt={2}>
                    <Alert severity="info">
                      Selected sheet: <strong>{selectedSheet.name}</strong>
                      <br />
                      Current month tab: <strong>{selectedTab}</strong>
                      <br />
                      This mapping will be applied to all monthly tabs in the sheet.
                    </Alert>
                  </Box>
                )}
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Configure Mapping */}
          <Step>
            <StepLabel>Configure Mapping</StepLabel>
            <StepContent>
              <Box py={2}>
                {selectedSheet && selectedTab && headers.length > 0 ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Map Columns from {selectedTab} for OTDR Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Map your sheet columns to OTDR fields for on-time delivery rate calculation
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        📊 <strong>All-Tabs Processing:</strong> The system will process data from all {sheetTabs.length} tabs in your sheet, not just "{selectedTab}". 
                        Each tab should have the same column structure you're mapping here.
                        <br />
                        <strong>Date Format Support:</strong> The system supports various date formats including DD.MM.YYYY (e.g., 23.09.2025).
                      </Typography>
                    </Alert>
                    
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Database Field</TableCell>
                            <TableCell>Required</TableCell>
                            <TableCell>Google Sheet Column</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {availableFields.map((field) => (
                            <TableRow key={field.key}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {field.label}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {field.required ? (
                                  <Chip label="Required" color="error" size="small" />
                                ) : (
                                  <Chip label="Optional" color="default" size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={columnMappings[field.key] || ''}
                                    onChange={(e) => handleColumnMapping(field.key, e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">-- Select Column --</MenuItem>
                                    {headers.map((header, index) => (
                                      <MenuItem key={index} value={header}>
                                        {header}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      Configuration Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Project</InputLabel>
                          <Select
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            label="Project"
                          >
                            {projects.map((project) => (
                              <MenuItem key={project.id} value={project.id}>
                                {project.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Configuration Name"
                          value={configName}
                          onChange={(e) => setConfigName(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Alert severity="info">
                    Please select a sheet and tab to configure column mappings.
                  </Alert>
                )}
              </Box>
            </StepContent>
          </Step>

          {/* Step 4: Save Configuration */}
          <Step>
            <StepLabel>Save Configuration</StepLabel>
            <StepContent>
              <Box py={2}>
                <Typography variant="h6" gutterBottom>
                  Review Configuration
                </Typography>
                
                <Card variant="outlined">
                  <CardContent>
                    <Typography><strong>Sheet:</strong> {selectedSheet?.name}</Typography>
                    <Typography><strong>Tab:</strong> {selectedTab}</Typography>
                    <Typography><strong>Project:</strong> {projects.find(p => p.id === selectedProject)?.name}</Typography>
                    <Typography><strong>Configuration Name:</strong> {configName}</Typography>
                    
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>Column Mappings:</Typography>
                      {Object.entries(columnMappings).map(([field, column]) => (
                        <Typography key={field} variant="body2" color="text.secondary">
                          • {availableFields.find(f => f.key === field)?.label} → {column}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 && (
          <Button 
            onClick={handleNext}
            variant="contained"
            endIcon={<NextIcon />}
            disabled={
              (activeStep === 0 && !isAuthenticated) ||
              (activeStep === 1 && !selectedSheet) ||
              (activeStep === 2 && headers.length === 0)
            }
          >
            {activeStep === 1 && sheets.length === 0 ? 'Load Sheets' : 'Next'}
          </Button>
        )}
        
        {activeStep === steps.length - 1 && (
          <Button 
            onClick={saveConfiguration}
            variant="contained"
            color="success"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleSheetsOAuthDialog;